// Copyright Prashant Vaghela, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0
import { FC, useCallback, useEffect, useState } from 'react';
import React from 'react';
import ReactFlow, {
  addEdge,
  Background,
  Connection,
  Edge,
  EdgeTypes,
  MiniMap,
  Node,
  NodeTypes,
  OnEdgesChange,
  OnNodesChange,
  Panel,
  Position,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from 'reactflow';
import dagre from 'dagre';

import {
  Button,
  ButtonDropdown,
  ButtonDropdownProps,
  Container,
  Icon,
  SpaceBetween,
} from '@cloudscape-design/components';

import { LineFlowItem } from '../../pages/flow/app';
import CustomEdge from './custoum-edges';
import CustomNode, {
  CustomNodeData,
  MPCPCustomNode,
  MPCPCustomNodeItem,
  MPCustomNode,
  MPCustomNodeData,
} from './custoum-node';

import 'reactflow/dist/style.css';

// --- Type Definitions for this file ---

// Union type for all possible data structures
type AllNodeData = CustomNodeData | MPCPCustomNodeItem | MPCustomNodeData;
type ProcessedNode = Node<AllNodeData>;

// Type for the data passed to the processing function
interface FlowItem {
  id: string;
  description: string;
  targets: string[];
  sequenceNumber: number;
  duration?: number;
  [key: string]: unknown; // Allow other properties from API data
}

// Type for the data used in EdgeLabel table
interface TableItem {
  process: string;
  time: string;
}

// Type for edge data which includes custom properties
interface CustomEdgeData {
  type: 'smoothstep' | 'bezier' | 'straight';
  strokeColor: number | undefined;
  endLabel?: string | TableItem[];
  startLabel?: string | TableItem[];
  item: FlowItem;
  isAnimationOn?: boolean;
}
type ProcessedEdge = Edge<CustomEdgeData>;

type Direction = 'LR' | 'TB';
type EdgeStyle = 'smoothstep' | 'bezier' | 'straight';

const nodeWidth = 200;
const nodeHeight = 100;

// Note: getLayoutedElements (non-dagre) is complex to generalize and may not produce desired results.
// For simplicity, I'll update it minimally but recommend using dagre for structured layouts.
const getLayoutedElements = (
  nodes: ProcessedNode[],
  edges: ProcessedEdge[],
  direction: Direction = 'TB',
): { nodes: ProcessedNode[]; edges: ProcessedEdge[] } => {
  const isHorizontal = direction === 'LR';
  // The provided non-dagre layout uses a sequence number grouping approach.
  // ... [Keep the original logic with updated types] ...

  // Group nodes by sequenceNumber
  const sequenceGroups: { [key: number]: ProcessedNode[] } = {};
  nodes.forEach(node => {
    const seq = node.data.sequenceNumber || 0;
    if (!sequenceGroups[seq]) {
      sequenceGroups[seq] = [];
    }
    sequenceGroups[seq].push(node);
  });

  const sortedSequences = Object.keys(sequenceGroups)
    .map(Number)
    .sort((a, b) => a - b);

  const horizontalGap = 500;
  const verticalGap = 400;

  // Position subsequent levels
  let offset = isHorizontal ? nodeWidth + horizontalGap : nodeHeight + verticalGap;

  const positionGroup = (group: ProcessedNode[], groupOffset: number) => {
    group.forEach((node, index) => {
      if (isHorizontal) {
        node.position = {
          x: offset,
          y: groupOffset + index * (nodeHeight + verticalGap),
        };
      } else {
        node.position = {
          x: groupOffset + index * (nodeWidth + horizontalGap),
          y: offset,
        };
      }
      node.targetPosition = (isHorizontal ? 'left' : 'top') as Position | undefined;
      node.sourcePosition = (isHorizontal ? 'right' : 'bottom') as Position | undefined;
    });
  };

  for (let i = 0; i < sortedSequences.length; i++) {
    const group = sequenceGroups[sortedSequences[i]];

    // Center nodes in the group
    const groupSize =
      group.length * (isHorizontal ? nodeHeight : nodeWidth) +
      (group.length - 1) * (isHorizontal ? verticalGap : horizontalGap);
    const groupOffset = -groupSize / 2 + (isHorizontal ? nodeHeight : nodeWidth) / 2;

    positionGroup(group, groupOffset);

    // Update offset for the next sequence group
    offset += isHorizontal ? nodeWidth + horizontalGap : nodeHeight + verticalGap;
  }

  return { nodes, edges };
};

const getDagreLayout = (
  nodes: ProcessedNode[],
  edges: ProcessedEdge[],
  direction: Direction = 'LR',
): { nodes: ProcessedNode[]; edges: ProcessedEdge[] } => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: direction, nodesep: 400, ranksep: 400 });

  const username = 'currentUser'; // Replace with actual username
  const storageKey = `flowPositions_${username}_${direction}`;
  const savedPositions = JSON.parse(localStorage.getItem(storageKey) || '{}') as Record<
    string,
    { x: number; y: number }
  >;

  nodes.forEach(node => {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });

  edges.forEach(edge => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  nodes.forEach(node => {
    const nodeWithPosition = dagreGraph.node(node.id);
    node.targetPosition = (direction === 'LR' ? 'left' : 'top') as Position | undefined;
    node.sourcePosition = (direction === 'LR' ? 'right' : 'bottom') as Position | undefined;
    // Construct a unique key for line-specific positioning
    const positionKey = `${node.id}-${node.data.line}`;

    // Use saved position if available, otherwise use dagre layout
    const position = savedPositions[positionKey] || {
      x: nodeWithPosition.x - nodeWidth / 2,
      y: nodeWithPosition.y - nodeHeight / 2,
    };

    node.position = position;
  });

  return { nodes, edges };
};

const processingData = (
  data: FlowItem[],
  direction: Direction,
  useDagre: boolean,
  edgeType: EdgeStyle,
): { nodes: ProcessedNode[]; edges: ProcessedEdge[] } => {
  const nodes: ProcessedNode[] = data.map(item => ({
    id: item.id,
    type: 'custom',
    data: {
      ...item,
      label: item.description || 'No Description',
    } as AllNodeData, // Cast to AllNodeData union type
    position: { x: 0, y: 0 },
  }));

  const edges: ProcessedEdge[] = [];

  const pendingProcesses: TableItem[] = [
    { process: 'Process A', time: '2:30' },
    { process: 'Process B', time: '1:45' },
    { process: 'Process C', time: '2:30' },
    { process: 'Process D', time: '1:45' },
    { process: 'Process E', time: '2:30' },
    { process: 'Process F', time: '1:45' },
    { process: 'Process G', time: '2:30' },
    { process: 'Process H', time: '1:45' },
    { process: 'Process I', time: '2:30' },
    { process: 'Process J', time: '1:45' },
    { process: 'Process K', time: '2:30' },
    { process: 'Process L', time: '1:45' },
    { process: 'Process A1', time: '2:30' },
    { process: 'Process B1', time: '1:45' },
    { process: 'Process C1', time: '2:30' },
    { process: 'Process D1', time: '1:45' },
    { process: 'Process E1', time: '2:30' },
    { process: 'Process F1', time: '1:45' },
    { process: 'Process G1', time: '2:30' },
    { process: 'Process H1', time: '1:45' },
    { process: 'Process I1', time: '2:30' },
    { process: 'Process J1', time: '1:45' },
    { process: 'Process K1', time: '2:30' },
    { process: 'Process L1', time: '1:45' },
    { process: 'Process A2', time: '2:30' },
    { process: 'Process B2', time: '1:45' },
    { process: 'Process C2', time: '2:30' },
    { process: 'Process D2', time: '1:45' },
    { process: 'Process E2', time: '2:30' },
    { process: 'Process F2', time: '1:45' },
    { process: 'Process G2', time: '2:30' },
    { process: 'Process H2', time: '1:45' },
    { process: 'Process I2', time: '2:30' },
    { process: 'Process J2', time: '1:45' },
    { process: 'Process K2', time: '2:30' },
    { process: 'Process L2', time: '1:45' },
  ];

  // Logic to create edges based on targets array
  data.forEach(item => {
    if (item.targets && Array.isArray(item.targets)) {
      item.targets.forEach(target => {
        const targetNode = data.find(node => node.id === target);
        if (targetNode) {
          const isBackward = item.sequenceNumber > targetNode.sequenceNumber;

          edges.push({
            id: `e${item.id}-${target}`,
            source: isBackward ? target : item.id,
            target: isBackward ? item.id : target,
            type: 'custom',
            animated: true,
            data: {
              type: edgeType,
              strokeColor: item.duration,
              endLabel: pendingProcesses,
              item: item,
            },
          } as ProcessedEdge); // Type assertion for the new Edge
        }
      });
    }
  });

  return useDagre ? getDagreLayout(nodes, edges, direction) : getLayoutedElements(nodes, edges, direction);
};

const edgeTypes: EdgeTypes = {
  custom: CustomEdge,
};

const nodeTypes: NodeTypes = {
  custom: CustomNode,
};

const mpcpnodeTypes: NodeTypes = {
  custom: MPCPCustomNode,
};

const mpcpdetailnodeTypes: NodeTypes = {
  custom: MPCustomNode,
};

// --- OperationFlow Component Definition ---

interface OperationFlowProps {
  data: LineFlowItem[];
  keys: string;
  isAnimationEdge?: boolean;
  ismpcpnodeTypes?: boolean;
  ismpcpdetailnodeTypes?: boolean;
}

const OperationFlow: FC<OperationFlowProps> = ({
  data,
  keys,
  isAnimationEdge = true,
  ismpcpnodeTypes = false,
  ismpcpdetailnodeTypes = false,
}) => {
  // Explicitly type the state
  const [nodes, setNodes, onNodesChange] = useNodesState<AllNodeData>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<CustomEdgeData>([]);
  const [useDagre, setUseDagre] = useState(true);
  const [edgeType, setEdgeType] = useState<EdgeStyle>('smoothstep');
  const [direction, setDirection] = useState<Direction>('LR');
  // useReactFlow returns an object with functions that can be used imperatively
  const { zoomIn, zoomOut, fitView } = useReactFlow();
  const [nodesDraggableState, setNodesDraggableState] = useState(false);
  const [isAnimationOn, setIsAnimationOn] = useState(isAnimationEdge);

  const updateLayout = useCallback(() => {
    const { nodes: layoutedNodes, edges: layoutedEdges } = processingData(data, direction, useDagre, edgeType);

    // Apply custom node data and type
    const typedLayoutedNodes: ProcessedNode[] = layoutedNodes.map(node => ({
      ...node,
      type: 'custom',
      data: { ...node.data, direction } as AllNodeData, // Cast the updated data
    }));

    // Apply custom edge data (e.g., animation status)
    const typedLayoutedEdges: ProcessedEdge[] = layoutedEdges.map(edge => ({
      ...edge,
      data: { ...edge.data, isAnimationOn } as CustomEdgeData, // Cast the updated data
    }));

    setNodes(typedLayoutedNodes);
    setEdges(typedLayoutedEdges);

    // Give ReactFlow a moment to render the nodes before fitting the view
    setTimeout(() => fitView(), 10);
  }, [data, direction, useDagre, isAnimationOn, fitView, edgeType, setNodes, setEdges]);

  // for the save position
  const [modifiedPositions, setModifiedPositions] = useState<Record<string, { x: number; y: number }>>({});

  const handleNodesChange: OnNodesChange = useCallback(
    (changes: any) => {
      onNodesChange(changes);

      // After the changes are applied, update modifiedPositions and save to localStorage
      // This is a common pattern to access the state after it's been updated by the hook
      setNodes((currentNodes: any[]) => {
        const newPositions: Record<string, { x: number; y: number }> = {};
        currentNodes.forEach(
          (node: { position: { x: number; y: number }; data: { line: any }; id: string | number }) => {
            if (node.position && node.data.line) {
              // Check for node.data.line existence before using it in the key
              newPositions[`${node.id}-${node.data.line}`] = node.position;
            } else if (node.position) {
              // Fallback key if line is not present, though line is used in getDagreLayout
              newPositions[node.id] = node.position;
            }
          },
        );

        // Use a functional update to merge current changes with previous positions
        setModifiedPositions(prev => {
          return { ...prev, ...newPositions };
        });

        return currentNodes;
      });
    },
    [onNodesChange, setNodes],
  );

  const savePositionsToLocalStorage = useCallback(() => {
    const username = 'currentUser'; // Replace with actual username
    const storageKey = `flowPositions_${username}_${direction}`;
    localStorage.setItem(storageKey, JSON.stringify(modifiedPositions));
  }, [modifiedPositions, direction]);

  useEffect(() => {
    updateLayout();
  }, [updateLayout]);

  const onConnect = useCallback((params: Connection) => setEdges((eds: any) => addEdge(params, eds)), [setEdges]);

  const toggleDirection = useCallback(() => {
    setDirection(prev => (prev === 'TB' ? 'LR' : 'TB'));
  }, []);

  const handleZoomIn = () => {
    zoomIn();
  };

  const handleZoomOut = () => {
    zoomOut();
  };

  const handleFitView = () => {
    fitView();
  };

  const toggleNodesDraggable = () => {
    setNodesDraggableState(!nodesDraggableState);
  };

  const toggleLayout = useCallback(() => {
    setUseDagre(prev => !prev);
  }, []);

  const [isExpanded, setIsExpanded] = useState(true);

  const toggleExpansion = () => {
    setIsExpanded(!isExpanded);
  };

  const cardTitle = (
    <div
      style={{
        width: '200px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        cursor: 'pointer',
      }}
      onClick={toggleExpansion}
    >
      <span>Options</span>
      <Icon name={!isExpanded ? 'angle-up' : 'angle-down'} />
    </div>
  );

  return (
    <ReactFlow
      key={`${keys}-${direction}`}
      nodes={nodes}
      edges={edges}
      onNodesChange={handleNodesChange as OnNodesChange} // Use the typed handler
      onEdgesChange={onEdgesChange as OnEdgesChange}
      onConnect={onConnect}
      nodeTypes={ismpcpnodeTypes ? mpcpnodeTypes : ismpcpdetailnodeTypes ? mpcpdetailnodeTypes : nodeTypes}
      edgeTypes={edgeTypes}
      nodesDraggable={nodesDraggableState}
      fitView
      minZoom={0}
    >
      <Background style={{ backgroundColor: '#F1F5F9' }} size={3} />
      <Panel position="top-right">
        <Panel position="top-right">
          <Container
            header={
              <div
                style={{
                  paddingTop: '5px',
                }}
              >
                {cardTitle}
              </div>
            }
            variant="default"
            disableContentPaddings
          >
            <div
              style={{
                paddingLeft: '16px',
                paddingRight: '16px',
                paddingBottom: '8px',
              }}
            >
              {isExpanded && (
                <SpaceBetween direction="horizontal" size="xs">
                  <Button
                    variant="normal"
                    iconName={direction === 'TB' ? 'view-horizontal' : 'view-vertical'}
                    onClick={toggleDirection}
                  />
                  <Button variant="normal" iconName="zoom-in" onClick={handleZoomIn} />
                  <Button variant="normal" iconName="zoom-out" onClick={handleZoomOut} />
                  <Button variant="normal" iconName="zoom-to-fit" onClick={handleFitView} />
                  <Button
                    variant="normal"
                    iconName={!nodesDraggableState ? 'lock-private' : 'unlocked'}
                    onClick={toggleNodesDraggable}
                  />
                  {isAnimationEdge && (
                    <Button
                      variant="normal"
                      iconName={isAnimationOn ? 'status-positive' : 'status-negative'}
                      onClick={() => setIsAnimationOn(!isAnimationOn)}
                    />
                  )}

                  <Button variant="normal" onClick={toggleLayout}>
                    Layout ({useDagre ? 'Dagre' : 'Centered'})
                  </Button>
                  <Button variant="normal" onClick={savePositionsToLocalStorage}>
                    Save
                  </Button>
                  <ButtonDropdown
                    variant="normal"
                    items={[
                      { text: 'Smooth', id: 'smoothstep' },
                      { text: 'Straight', id: 'straight' },
                      { text: 'Bezier', id: 'bezier' },
                    ]}
                    onItemClick={(item: ButtonDropdownProps.ItemClickDetails) =>
                      setEdgeType(item.detail.id as EdgeStyle)
                    }
                  >
                    Edges
                  </ButtonDropdown>
                </SpaceBetween>
              )}
            </div>
          </Container>
        </Panel>
      </Panel>
      <MiniMap zoomable pannable />
    </ReactFlow>
  );
};

// Wrapper component to provide the ReactFlow context (required by useReactFlow)
const OperationFlowWithProvider: FC<OperationFlowProps> = props => (
  <ReactFlowProvider>
    <OperationFlow {...props} />
  </ReactFlowProvider>
);

export default OperationFlowWithProvider;
